import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';
import { useAuth } from '@contexts/useAuth';
import { IssueStatus } from '@d-orca/shared/trusstor';
import { useAddTextComment, useQuickStatusUpdate } from '@components/Pages/IssuesPage/hooks';
import { ReviewDialog } from '@components/ui/ReviewDialog/ReviewDialog';

export interface IIssueReviewDialogProps {
  onClose: () => void;
  siteId: string | null;
  issueId: string;
}

/**
 * A manager's review of an issue waiting for review (issue-review-stage spec, ruling 3): Approve completes it; Reject
 * turns it rejected and posts the reason as a text comment on it — Trusstor's own flow, so no new field.
 *
 * Both are awaited, and a failure keeps the dialog open with its message. Reject writes the status FIRST: a reason
 * posted on an issue that then stays in review is an orphan, and a retry would post it twice. Once the status is
 * written, a retry only re-sends the reason.
 */
export function IssueReviewDialog({ onClose, siteId, issueId }: IIssueReviewDialogProps) {
  const { t } = useTranslation();
  // The dialog reports both failures itself.
  const status = useQuickStatusUpdate(siteId, OWNS_ERROR_FEEDBACK);
  const comment = useAddTextComment(siteId, issueId, useAuth().user?.username, OWNS_ERROR_FEEDBACK);
  const rejected = useRef(false);

  return (
    <ReviewDialog
      onClose={onClose}
      title={t('activities.issueStatus.reviewTitle')}
      reasonPlaceholder={t('activities.issueStatus.rejectReasonPlaceholder')}
      rejectError={t('activities.issueStatus.rejectError')}
      approveError={t('activities.issueStatus.approveError')}
      onApprove={() => status.mutateAsync({ issueId, status: IssueStatus.COMPLETED })}
      onReject={async (reason) => {
        if (!rejected.current) {
          await status.mutateAsync({ issueId, status: IssueStatus.REJECTED });
          rejected.current = true;
        }
        await comment.mutateAsync(reason);
      }}
    />
  );
}
